import { BufferGeometry, Float32BufferAttribute, LineBasicMaterial, LineSegments, Sphere, Vector3 } from 'three';
import { pixelRatio2d } from '@/editor/math/const';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { Curve2dUtils } from '@/editor/math/Curve2dUtils';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { globalStores } from '@/editor/stores';
import { ENTITY_RENDER_ORDER } from '@/editor/constant/entity';
import BaseModel2d from './BaseModel2d';
import { ITextModelInfo, TextModel } from './textModel';
import { DEFAULT_RULER_OFFSET } from '@/editor/constant/ruler';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';

export interface IRulerInfo {
  id: string;
  start: IPoint2d;
  end: IPoint2d;
  /** 偏移量，对 `start`, `end` 进行偏移，正数为左偏移 */
  offset?: number;
  /** 起点到终点距离是否可编辑 */
  editable?: boolean;
  focusTrap?: boolean;
  center?: IPoint2d;
  clockwise?: boolean;
  /** 弦高是否可编辑 */
  sagittaEditable?: boolean;
  max?: number;
  blurOnCommit?: boolean;
}

interface IRulerModelParams<T> {
  /** 修改值 */
  onCommit?: (id: string, value: number, rulerInfo: T) => void;
  /** 提交值 */
  onChange?: (id: string, value: number, rulerInfo: T) => void;
}

export class RulerModel<T extends IRulerInfo = IRulerInfo> extends BaseModel2d {
  static lineMaterial = new LineBasicMaterial({ color: 0x262829, userData: { shared: true } });

  private _rulerInfos: T[] = [];
  private _onChange?: IRulerModelParams<T>['onChange'];
  private _onCommit?: IRulerModelParams<T>['onCommit'];
  private _textInfos: ITextModelInfo[] = [];
  private _textModel: TextModel;

  constructor(params?: IRulerModelParams<T>) {
    super();

    this._onChange = params?.onChange;
    this._onCommit = params?.onCommit;
    this._textModel = new TextModel({
      onChange: this._handleChange,
      onCommit: this._handleCommit,
      onFocus: (id?: string) => {
        if (id) {
          globalStores.editorStateStore.setTextModelFocusId(id);
        }
      }
    });
    globalStores.editorStateStore.sceneInfo.scene2d.scene.add(this._modelGroup);
    this.setDisplayOrder(ENTITY_RENDER_ORDER.ruler);
  }

  public updateModel(rulerInfos: T[], initFocusId?: string) {
    this.clear();

    if (!rulerInfos.length) return;

    this._rulerInfos = rulerInfos;
    this._createLineModel();

    if (initFocusId !== undefined && globalStores.editorStateStore.textModelFocusId === undefined) {
      globalStores.editorStateStore.setTextModelFocusId(initFocusId);
    }

    this._textModel.createModel(this._textInfos, globalStores.editorStateStore.textModelFocusId);
  }

  public resetFocusId() {
    globalStores.editorStateStore.resetTextModelFocusData();
  }

  private _createLineModel() {
    const lineRulerInfos = this._rulerInfos;

    const lineGmt = new BufferGeometry();
    const lineFloat32Array = new Float32Array(lineRulerInfos.length * 20);
    const assistLineOffsetValue = 50;
    const assistSlashLineOffsetValue = (assistLineOffsetValue * Math.SQRT2) / 2;

    for (let i = 0; i < lineRulerInfos.length; i++) {
      const rulerInfo = lineRulerInfos[i];
      let start = rulerInfo.start;
      let end = rulerInfo.end;

      // 弦高处理
      if ('center' in rulerInfo && rulerInfo.center && rulerInfo.clockwise !== undefined) {
        const { start: arcStart, end: arcEnd, center, clockwise } = rulerInfo;
        const chordMidPoint = Point2dUtils.getCenter(arcStart, arcEnd);

        // 弦高数据，绘制过程中，还没确定弦长的情况下，不传入center
        const arcSeg = new ArcSeg2d({ start: arcStart, end: arcEnd, center, clockwise: clockwise });
        const arcMidPoint = arcSeg.getMidPoint();

        start = chordMidPoint;
        end = arcMidPoint;
      }

      const lineSeg = new LineSeg2d(start, end);
      const lineOffsetValue = typeof rulerInfo.offset === 'number' ? rulerInfo.offset : DEFAULT_RULER_OFFSET;
      const offsetLine = Curve2dUtils.offsetCurve(lineSeg, -lineOffsetValue);

      lineFloat32Array.set(getRulerSegPointArr(offsetLine, assistLineOffsetValue, assistSlashLineOffsetValue), i * 20);

      this._textInfos.push({
        position: { x: (offsetLine.start.x + offsetLine.end.x) / 2, y: (offsetLine.start.y + offsetLine.end.y) / 2 },
        id: rulerInfo.id,
        value: Point2dUtils.distanceTo(offsetLine.start, offsetLine.end).toFixed(0),
        editable: rulerInfo.editable,
        trapFocus: rulerInfo.focusTrap,
        blurOnCommit: rulerInfo.blurOnCommit,
        max: rulerInfo.max
      });
    }
    lineGmt.setAttribute('position', new Float32BufferAttribute(lineFloat32Array, 2));

    // 由于没有传入z值，需要手动添加boundingsphere，防止报错
    lineGmt.boundingSphere = new Sphere(new Vector3(0, 0, 0), 1);
    const line = new LineSegments(lineGmt, RulerModel.lineMaterial);
    line.frustumCulled = false;
    this._modelGroup.add(line);
  }

  private _handleChange = (id: string, value: number) => {
    if (!id) return;

    const rulerInfo = this._rulerInfos.find((ri) => ri.id === id);
    if (!rulerInfo) return;

    this._onChange?.(id, value, rulerInfo);
  };

  private _handleCommit = (id: string, value: number) => {
    if (!id) return;

    const rulerInfo = this._rulerInfos.find((ri) => 'id' in ri && ri.id === id);
    if (!rulerInfo) return;

    this._onCommit?.(id, value, rulerInfo);
  };

  override clear() {
    this._rulerInfos.length = 0;
    this._textInfos.length = 0;
    this._textModel.clear();
    super.clear();
  }

  override dispose() {
    this.clear();
    this._textModel.dispose();
    super.dispose();
  }
}

function getRulerSegPointArr(offsetLine: LineSeg2d, assistLineOffsetValue: number, assistSlashLineOffsetValue: number) {
  const direction = offsetLine.getDirectionVector();
  const verticalDirection = offsetLine.getPerpendicularDirectionVector();
  const points = [];

  // 起点到终点连线
  let start = offsetLine.start;
  let end = offsetLine.end;
  points[0] = start.x / pixelRatio2d;
  points[1] = start.y / pixelRatio2d;
  points[2] = end.x / pixelRatio2d;
  points[3] = end.y / pixelRatio2d;

  // 起点辅助线
  start = Point2dUtils.addDistance(offsetLine.start, assistLineOffsetValue, verticalDirection);
  end = Point2dUtils.addDistance(offsetLine.start, -assistLineOffsetValue, verticalDirection);
  points[4] = start.x / pixelRatio2d;
  points[5] = start.y / pixelRatio2d;
  points[6] = end.x / pixelRatio2d;
  points[7] = end.y / pixelRatio2d;

  // 起点辅助斜线
  start = Point2dUtils.addDistance(offsetLine.start, assistSlashLineOffsetValue, direction);
  start = Point2dUtils.addDistance(start, assistSlashLineOffsetValue, verticalDirection);
  end = Point2dUtils.addDistance(offsetLine.start, -assistSlashLineOffsetValue, direction);
  end = Point2dUtils.addDistance(end, -assistSlashLineOffsetValue, verticalDirection);
  points[8] = start.x / pixelRatio2d;
  points[9] = start.y / pixelRatio2d;
  points[10] = end.x / pixelRatio2d;
  points[11] = end.y / pixelRatio2d;

  // 终点辅助线
  start = Point2dUtils.addDistance(offsetLine.end, assistLineOffsetValue, verticalDirection);
  end = Point2dUtils.addDistance(offsetLine.end, -assistLineOffsetValue, verticalDirection);
  points[12] = start.x / pixelRatio2d;
  points[13] = start.y / pixelRatio2d;
  points[14] = end.x / pixelRatio2d;
  points[15] = end.y / pixelRatio2d;

  // 终点辅助斜线
  start = Point2dUtils.addDistance(offsetLine.end, assistSlashLineOffsetValue, direction);
  start = Point2dUtils.addDistance(start, assistSlashLineOffsetValue, verticalDirection);
  end = Point2dUtils.addDistance(offsetLine.end, -assistSlashLineOffsetValue, direction);
  end = Point2dUtils.addDistance(end, -assistSlashLineOffsetValue, verticalDirection);
  points[16] = start.x / pixelRatio2d;
  points[17] = start.y / pixelRatio2d;
  points[18] = end.x / pixelRatio2d;
  points[19] = end.y / pixelRatio2d;

  return points;
}
